import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { MapPin, Palette, ArrowRight, ArrowLeft, Compass, Landmark, BookOpen, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { getRegionBySlug, getOtherRegions } from '@/data/silkRoadRegions';
import { getLandmarkImages } from '@/data/landmarkImages';
import { getImageUrl } from '@/utils/helpers';

const API = process.env.REACT_APP_BACKEND_URL || '';

export default function RegionExplorePage() {
  const { regionSlug } = useParams();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [regionArtists, setRegionArtists] = useState([]);
  const [loading, setLoading] = useState(true);

  const region = getRegionBySlug(regionSlug);
  const otherRegions = getOtherRegions(regionSlug);

  useEffect(() => {
    if (!region) return;
    setLoading(true);
    axios.get(`${API}/api/featured-artists/region/${regionSlug}`)
      .then(res => setRegionArtists(res.data?.artists || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [regionSlug, region]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [regionSlug]);

  // SEO: Set document title and meta description
  useEffect(() => {
    if (!region) return;
    const name = t(`silkRoad.regions.${regionSlug}.name`);
    const badge = t('silkRoad.badge');
    document.title = `${name} - ${badge} | ArtCanvas`;
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute('content',
        t(`silkRoad.regions.${regionSlug}.extendedDescription`,
          t(`silkRoad.regions.${regionSlug}.description`)).substring(0, 160));
    }
    return () => { document.title = 'ArtCanvas'; };
  }, [regionSlug, region, t]);

  // SEO: Inject JSON-LD structured data
  useEffect(() => {
    if (!region) return;
    const name = t(`silkRoad.regions.${regionSlug}.name`);
    const desc = t(`silkRoad.regions.${regionSlug}.extendedDescription`,
      t(`silkRoad.regions.${regionSlug}.description`));
    const tagline = t(`silkRoad.regions.${regionSlug}.tagline`);
    const pageUrl = `${window.location.origin}/explore/${regionSlug}`;

    const landmarks = [0, 1, 2].map(i => ({
      name: t(`silkRoad.regions.${regionSlug}.landmarks.${i}.name`, ''),
      description: t(`silkRoad.regions.${regionSlug}.landmarks.${i}.description`, ''),
    })).filter(l => l.name);

    const attractionSchemas = landmarks.map(l => ({
      "@type": "TouristAttraction",
      "name": l.name,
      "description": l.description,
      "isAccessibleForFree": true,
      "touristType": ["Cultural tourism", "Art tourism"]
    }));

    const jsonLd = [
      {
        "@context": "https://schema.org",
        "@type": "TouristDestination",
        "name": `${name} - ${tagline}`,
        "description": desc.substring(0, 500),
        "url": pageUrl,
        "image": region.heroImage,
        "touristType": ["Cultural tourism", "Art tourism", "Silk Road heritage"],
        "containsPlace": attractionSchemas,
        "isPartOf": {
          "@type": "Place",
          "name": "Silk Road Cultural Region",
          "description": t('silkRoad.subtitle')
        }
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "ArtCanvas", "item": window.location.origin },
          { "@type": "ListItem", "position": 2, "name": t('silkRoad.badge'), "item": `${window.location.origin}/#silk-road` },
          { "@type": "ListItem", "position": 3, "name": name, "item": pageUrl }
        ]
      },
      {
        "@context": "https://schema.org",
        "@type": "WebPage",
        "name": `${name} - ${t('silkRoad.badge')} | ArtCanvas`,
        "description": desc.substring(0, 160),
        "url": pageUrl,
        "inLanguage": i18n.language,
        "isPartOf": { "@type": "WebSite", "name": "ArtCanvas", "url": window.location.origin },
        "about": {
          "@type": "Place",
          "name": name,
          "description": desc.substring(0, 300)
        }
      }
    ];

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = `jsonld-region-${regionSlug}`;
    script.textContent = JSON.stringify(jsonLd);

    const existing = document.getElementById(`jsonld-region-${regionSlug}`);
    if (existing) existing.remove();
    document.head.appendChild(script);

    return () => {
      const el = document.getElementById(`jsonld-region-${regionSlug}`);
      if (el) el.remove();
    };
  }, [regionSlug, region, t, i18n.language]);

  if (!region) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-amber-50 pt-16">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-slate-900 mb-4">Region not found</h1>
          <Button onClick={() => navigate('/')}>Back to Home</Button>
        </div>
      </div>
    );
  }

  const regionName = t(`silkRoad.regions.${regionSlug}.name`);
  const tagline = t(`silkRoad.regions.${regionSlug}.tagline`);
  const landmarkImages = getLandmarkImages(regionSlug);
  const landmarks = [0, 1, 2].map(i => ({
    name: t(`silkRoad.regions.${regionSlug}.landmarks.${i}.name`, ''),
    description: t(`silkRoad.regions.${regionSlug}.landmarks.${i}.description`, ''),
    image: landmarkImages[i] || '',
  })).filter(l => l.name);

  return (
      <div className="min-h-screen bg-amber-50 pt-16" data-testid="region-explore-page">
        {/* Hero Section */}
        <section className="relative h-[60vh] min-h-[400px] overflow-hidden" data-testid="region-hero">
          <img
            src={region.heroImage}
            alt={regionName}
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-slate-950/90 via-slate-950/40 to-slate-950/20" />

          <div className="absolute inset-0 flex flex-col justify-end px-6 pb-12 md:pb-16">
            <div className="max-w-5xl mx-auto w-full">
              {/* Breadcrumb */}
              <nav className="flex items-center gap-2 text-sm text-white/60 mb-6" data-testid="region-breadcrumb">
                <Link to="/" className="hover:text-white transition-colors">Home</Link>
                <span>/</span>
                <Link to="/explore" className="text-amber-400 hover:text-amber-300 transition-colors">{t('silkRoad.badge')}</Link>
                <span>/</span>
                <span className="text-white">{regionName}</span>
              </nav>

              <p className="text-amber-400 text-sm tracking-[0.2em] uppercase font-medium mb-3">
                {tagline}
              </p>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-4 tracking-tight" data-testid="region-name-heading">
                {regionName}
              </h1>
              <p className="text-white/70 text-lg max-w-2xl">
                {t(`silkRoad.regions.${regionSlug}.description`)}
              </p>
            </div>
          </div>
        </section>

        {/* Cultural Overview */}
        <section className="py-16 md:py-20 px-6" data-testid="region-cultural-overview">
          <div className="max-w-3xl mx-auto">
            <div className="flex items-center gap-3 mb-8">
              <BookOpen className="w-5 h-5 text-amber-700" />
              <h2 className="text-2xl md:text-3xl font-bold text-slate-900">
                {t('silkRoad.explore.culturalOverview', 'Cultural Overview')}
              </h2>
            </div>
            <div className="prose prose-lg prose-slate max-w-none">
              <p className="text-slate-700 leading-relaxed text-base md:text-lg whitespace-pre-line">
                {t(`silkRoad.regions.${regionSlug}.extendedDescription`,
                   t(`silkRoad.regions.${regionSlug}.description`))}
              </p>
            </div>
          </div>
        </section>

        {/* Key Landmarks */}
        {landmarks.length > 0 && (
          <section className="bg-slate-900 py-16 md:py-20 px-6" data-testid="region-landmarks">
            <div className="max-w-5xl mx-auto">
              <div className="flex items-center gap-3 mb-10">
                <Landmark className="w-5 h-5 text-amber-400" />
                <h2 className="text-2xl md:text-3xl font-bold text-white">
                  {t('silkRoad.explore.landmarks', 'Cultural Heritage Sites')}
                </h2>
              </div>
              <div className="grid md:grid-cols-3 gap-6">
                {landmarks.map((landmark, idx) => (
                  <div
                    key={idx}
                    className="bg-white/5 border border-white/10 rounded-2xl overflow-hidden hover:bg-white/10 transition-colors duration-300 group"
                    data-testid={`landmark-${idx}`}
                  >
                    {landmark.image && (
                      <div className="aspect-[16/10] overflow-hidden">
                        <img
                          src={landmark.image}
                          alt={landmark.name}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          loading="lazy"
                        />
                      </div>
                    )}
                    <div className="p-5">
                      <div className="flex items-center gap-2 mb-3">
                        <span className="w-7 h-7 rounded-full bg-amber-500/20 flex items-center justify-center text-amber-400 font-bold text-xs flex-shrink-0">
                          {String(idx + 1).padStart(2, '0')}
                        </span>
                        <h3 className="text-white font-bold text-base leading-tight">{landmark.name}</h3>
                      </div>
                      <p className="text-slate-400 text-sm leading-relaxed">{landmark.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Art Traditions */}
        <section className="py-16 md:py-20 px-6" data-testid="region-art-traditions">
          <div className="max-w-3xl mx-auto">
            <div className="flex items-center gap-3 mb-8">
              <Palette className="w-5 h-5 text-amber-700" />
              <h2 className="text-2xl md:text-3xl font-bold text-slate-900">
                {t('silkRoad.explore.artTraditions', 'Artistic Heritage')}
              </h2>
            </div>
            <p className="text-slate-700 leading-relaxed text-base md:text-lg whitespace-pre-line">
              {t(`silkRoad.regions.${regionSlug}.artTraditions`,
                 t(`silkRoad.regions.${regionSlug}.description`))}
            </p>
          </div>
        </section>

        {/* Featured Artists */}
        <section className="bg-slate-50 py-16 md:py-20 px-6" data-testid="region-artists">
          <div className="max-w-5xl mx-auto">
            <div className="flex items-center gap-3 mb-10">
              <Users className="w-5 h-5 text-amber-700" />
              <h2 className="text-2xl md:text-3xl font-bold text-slate-900">
                {t('silkRoad.explore.artistsTitle', 'Artists from {{region}}').replace('{{region}}', regionName)}
              </h2>
            </div>

            {loading ? (
              <div className="text-center py-12 text-slate-400">Loading...</div>
            ) : regionArtists.length > 0 ? (
              <div className="space-y-8">
                {regionArtists.map(artist => (
                  <div key={artist.id} className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6" data-testid={`region-artist-${artist.id}`}>
                    <div className="flex items-center gap-4 mb-4">
                      <div className="w-14 h-14 rounded-full bg-amber-100 overflow-hidden flex-shrink-0">
                        {artist.user.avatar ? (
                          <img src={getImageUrl(artist.user.avatar)} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-amber-700 font-bold text-lg">
                            {artist.user.name?.charAt(0) || '?'}
                          </div>
                        )}
                      </div>
                      <div>
                        <h3 className="text-lg font-bold text-slate-900">{artist.user.name}</h3>
                        {artist.tagline && <p className="text-sm text-slate-500">{artist.tagline}</p>}
                        <p className="text-xs text-amber-600 mt-0.5">{artist.artwork_count} {t('silkRoad.explore.works', 'works')}</p>
                      </div>
                    </div>
                    {artist.artworks?.length > 0 && (
                      <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
                        {artist.artworks.map(aw => (
                          <div
                            key={aw.id}
                            className="aspect-square rounded-lg overflow-hidden cursor-pointer group"
                            onClick={() => navigate(`/artwork/${aw.id}`)}
                          >
                            <img
                              src={getImageUrl(aw.image_url)}
                              alt={aw.title}
                              className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                              loading="lazy"
                            />
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-16 bg-white rounded-2xl border border-dashed border-slate-200">
                <div className="w-16 h-16 rounded-full bg-amber-50 flex items-center justify-center mx-auto mb-4">
                  <Palette className="w-8 h-8 text-amber-500" />
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-2">
                  {t('silkRoad.explore.noArtistsTitle', 'Be the First Artist')}
                </h3>
                <p className="text-slate-500 text-sm mb-6 max-w-md mx-auto">
                  {t('silkRoad.explore.noArtistsDesc', 'No artists from this region have joined yet. Be the pioneer and share your art with collectors worldwide.')}
                </p>
                <Button
                  className="bg-amber-600 hover:bg-amber-500 text-white"
                  onClick={() => navigate('/login')}
                  data-testid="region-join-btn"
                >
                  <Palette className="w-4 h-4 mr-2" />
                  {t('silkRoad.joinAsArtist')}
                </Button>
              </div>
            )}
          </div>
        </section>

        {/* Explore Other Regions */}
        <section className="bg-slate-950 py-16 md:py-20 px-6" data-testid="region-other-regions">
          <div className="max-w-5xl mx-auto">
            <div className="flex items-center gap-3 mb-10">
              <Compass className="w-5 h-5 text-amber-400" />
              <h2 className="text-2xl md:text-3xl font-bold text-white">
                {t('silkRoad.explore.otherRegions', 'Explore Other Regions')}
              </h2>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
              {otherRegions.map(r => (
                <Link
                  key={r.slug}
                  to={`/explore/${r.slug}`}
                  className="group relative overflow-hidden rounded-xl aspect-[3/4]"
                  data-testid={`other-region-${r.slug}`}
                >
                  <img
                    src={r.image}
                    alt={t(`silkRoad.regions.${r.slug}.name`)}
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-3">
                    <p className="text-amber-400 text-[9px] tracking-wider uppercase font-medium">
                      {t(`silkRoad.regions.${r.slug}.tagline`)}
                    </p>
                    <h3 className="text-white text-sm font-bold leading-tight">
                      {t(`silkRoad.regions.${r.slug}.name`)}
                    </h3>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Bottom CTA */}
        <section className="py-16 px-6 text-center bg-amber-50" data-testid="region-bottom-cta">
          <div className="max-w-2xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-4">
              {t('silkRoad.explore.ctaTitle', 'Discover Art from the Silk Road')}
            </h2>
            <p className="text-slate-600 mb-8">
              {t('silkRoad.explore.ctaDesc', 'Browse artworks from artists across the Silk Road cultural sphere, or join as an artist to share your work with the world.')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                size="lg"
                className="bg-amber-600 hover:bg-amber-500 text-white px-8 py-6 rounded-xl"
                onClick={() => navigate('/artcanvas')}
                data-testid="region-explore-gallery-btn"
              >
                {t('silkRoad.exploreGallery')}
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="border-slate-300 px-8 py-6 rounded-xl"
                onClick={() => navigate('/')}
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                {t('silkRoad.explore.backToHome', 'Back to Home')}
              </Button>
            </div>
          </div>
        </section>
      </div>
  );
}
